'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import type { MenuCategory } from '@/services/category.service';
interface MegaMenuProps {
  categories: MenuCategory[];
}
const LEGACY_SHIELD_HUB_SLUG = 'scw-shield-alarm-hub-and-pin-pad-bundles.php';
const CANONICAL_SHIELD_HUB_SLUG = 'scw-shield-alarm-hub-and-pin-pad-bundles';
function normalizeLegacyMenuUrl(url: string): string {
  return url.replace(LEGACY_SHIELD_HUB_SLUG, CANONICAL_SHIELD_HUB_SLUG);
}

/**
 * Get the href for a menu category
 * Handles custom URLs, section headers (#), and external links
 */
function getCategoryHref(category: MenuCategory): string | null {
  const {
    customUrl,
    slug
  } = category;

  // No custom URL - use clean URL
  if (!customUrl) {
    return `/${slug}`;
  }
  const normalizedUrl = normalizeLegacyMenuUrl(customUrl);

  // Section header (no link)
  if (normalizedUrl === '#') {
    return null;
  }

  // External URL
  if (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://')) {
    return normalizedUrl;
  }

  // Internal URL (starts with /)
  if (normalizedUrl.startsWith('/')) {
    return normalizedUrl;
  }

  // Fallback - treat as internal path
  return `/${normalizedUrl}`;
}

/**
 * Check if URL is external
 */
function isExternalUrl(url: string | null): boolean {
  if (!url) return false;
  return url.startsWith('http://') || url.startsWith('https://');
}
export function MegaMenu(t0) {
  const $ = _c(20);
  const {
    categories
  } = t0;
  const [activeCategory, setActiveCategory] = useState(null);
  const timeoutRef = useRef(null);
  const handleMouseEnter = categoryId => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveCategory(categoryId);
  };
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      timeoutRef.current = setTimeout(() => {
        setActiveCategory(null);
      }, 150);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleMouseLeave = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== activeCategory || $[4] !== categories) {
    let t5;
    if ($[6] !== activeCategory) {
      t5 = category => category.id === activeCategory && category.children.length > 0;
      $[6] = activeCategory;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    t4 = categories.find(t5);
    $[3] = activeCategory;
    $[4] = categories;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const activeItem = t4;
  const t5 = "mega-menu-nav";
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = "mega-menu-list";
  const t8 = categories.map(category_0 => {
    const href = getCategoryHref(category_0);
    const isExternal = isExternalUrl(href);
    return <li key={category_0.id} onMouseEnter={() => handleMouseEnter(category_0.id)}>{href ? <Link href={href} target={isExternal ? "_blank" : undefined} rel={isExternal ? "noopener noreferrer" : undefined} className={cn("mega-menu-link", activeCategory === category_0.id && "is-active")}>{category_0.name}{category_0.children.length > 0 && <ChevronDownIcon className={cn("h-3.5 w-3.5 text-[#ED8326] transition-transform", activeCategory === category_0.id && "rotate-180")} />}</Link> : <span className={cn("mega-menu-link cursor-default", activeCategory === category_0.id && "is-active")}>{category_0.name}{category_0.children.length > 0 && <ChevronDownIcon className={cn("h-3.5 w-3.5 text-[#ED8326] transition-transform", activeCategory === category_0.id && "rotate-180")} />}</span>}</li>;
  });
  let t9;
  if ($[9] !== t8) {
    t9 = <ul className={t7}>{t8}</ul>;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== activeItem || $[12] !== handleMouseEnter || $[13] !== handleMouseLeave) {
    t10 = activeItem && <MegaMenuDropdown category={activeItem} onMouseEnter={() => handleMouseEnter(activeItem.id)} onMouseLeave={handleMouseLeave} />;
    $[11] = activeItem;
    $[12] = handleMouseEnter;
    $[13] = handleMouseLeave;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== handleMouseLeave || $[16] !== t10 || $[17] !== t6 || $[18] !== t9) {
    t11 = <nav className={t5} onMouseLeave={handleMouseLeave} onMouseEnter={t6}>{t9}{t10}</nav>;
    $[15] = handleMouseLeave;
    $[16] = t10;
    $[17] = t6;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
}
interface MegaMenuDropdownProps {
  category: MenuCategory;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}
function MegaMenuDropdown(t0) {
  const $ = _c(26);
  const {
    category,
    onMouseEnter,
    onMouseLeave
  } = t0;
  const childCount = category.children.length;
  const columnCount = Math.min(childCount || 1, 6);
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== category || $[1] !== columnCount || $[2] !== onMouseEnter || $[3] !== onMouseLeave) {
    const categoryHref = getCategoryHref(category);
    t4 = "mega-menu-panel";
    t5 = onMouseEnter;
    t6 = onMouseLeave;
    t1 = "mega-menu-inner";
    const t7 = `repeat(${columnCount}, minmax(150px, 1fr))`;
    let t8;
    if ($[10] !== t7) {
      t8 = {
        gridTemplateColumns: t7
      };
      $[10] = t7;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    let t9;
    if ($[12] !== category.children) {
      t9 = category.children.map(_temp2);
      $[12] = category.children;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    if ($[14] !== t8 || $[15] !== t9) {
      t2 = <div className="mega-menu-columns" style={t8}>{t9}</div>;
      $[14] = t8;
      $[15] = t9;
      $[16] = t2;
    } else {
      t2 = $[16];
    }
    t3 = categoryHref && <div className="mega-menu-footer"><Link href={categoryHref} target={isExternalUrl(categoryHref) ? "_blank" : undefined} rel={isExternalUrl(categoryHref) ? "noopener noreferrer" : undefined} className="mega-menu-footer-link">View All {category.name}</Link></div>;
    $[0] = category;
    $[1] = columnCount;
    $[2] = onMouseEnter;
    $[3] = onMouseLeave;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[17] !== t1 || $[18] !== t2 || $[19] !== t3) {
    t7 = <div className={t1}>{t2}{t3}</div>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
    t8 = <div className={t4} onMouseEnter={t5} onMouseLeave={t6}>{t7}</div>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}
function _temp2(child, index) {
  const childHref = getCategoryHref(child);
  const isChildExternal = isExternalUrl(childHref);
  return <div key={child.id} className={cn("mega-menu-column", index === 0 && "mega-menu-column--featured")}>{childHref ? <Link href={childHref} target={isChildExternal ? "_blank" : undefined} rel={isChildExternal ? "noopener noreferrer" : undefined} className="mega-menu-heading">{child.name}</Link> : <span className="mega-menu-heading">{child.name}</span>}{child.children.length > 0 && <ul className="mega-menu-items">{child.children.map(_temp)}</ul>}</div>;
}
function _temp(grandchild) {
  const grandchildHref = getCategoryHref(grandchild);
  const isGrandchildExternal = isExternalUrl(grandchildHref);
  return <li key={grandchild.id} className="mega-menu-item">{grandchildHref ? <Link href={grandchildHref} target={isGrandchildExternal ? "_blank" : undefined} rel={isGrandchildExternal ? "noopener noreferrer" : undefined} className="mega-menu-link-item">{grandchild.name}</Link> : <span className="mega-menu-link-item">{grandchild.name}</span>}</li>;
}
function ChevronDownIcon(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="m6 9 6 6 6-6" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}